import { HTTPError } from "ky";

import type { ApiError } from "~/shared/types/api-types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === "object" && !Array.isArray(value);

const getTrimmedString = (...values: unknown[]): string => {
	for (const value of values) {
		if (typeof value === "string") {
			const trimmed = value.trim();
			if (trimmed.length > 0) {
				return trimmed;
			}
		}
	}
	return "";
};

export const isJsonContentType = (contentType: string): boolean => {
	const normalized = contentType.toLowerCase();
	return (
		normalized.includes("application/json") ||
		normalized.includes("text/json") ||
		normalized.includes("+json")
	);
};

const GENERIC_ERROR_MESSAGES = new Set([
	"validation failed",
	"one or more validation errors occurred",
	"one or more validation errors occurred.",
]);

function parseNestedError(entry: string): string[] {
	const trimmed = entry.trim();
	if (trimmed.length === 0) {
		return [];
	}

	if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
		try {
			const parsed: unknown = JSON.parse(trimmed);
			const extracted = extractFromJson(parsed);
			if (extracted) {
				return extracted;
			}
			// If it's valid JSON but no standard error fields found,
			// it's likely a technical dump. Return empty so we don't show raw JSON.
			return [];
		} catch {
			// Not valid JSON, fall through
		}
	}

	return [trimmed];
}

function extractMessageFromEntry(entry: unknown): string[] {
	if (typeof entry === "string") {
		return parseNestedError(entry);
	}
	if (isRecord(entry)) {
		return extractFromJson(entry) ?? [];
	}
	return [];
}

function extractErrorsValue(errors: unknown): string[] {
	if (Array.isArray(errors)) {
		return errors.flatMap((entry) => extractMessageFromEntry(entry));
	}

	if (isRecord(errors)) {
		return Object.values(errors).flatMap((entry) =>
			Array.isArray(entry)
				? entry.flatMap((nestedEntry) => extractMessageFromEntry(nestedEntry))
				: extractMessageFromEntry(entry),
		);
	}

	return extractMessageFromEntry(errors);
}

const extractFromJson = (parsed: unknown): string[] | null => {
	if (!isRecord(parsed)) {
		return null;
	}

	const errors = parsed["errors"] ?? parsed["Errors"];
	const errorMessages = extractErrorsValue(errors);
	if (errorMessages.length > 0) {
		return errorMessages;
	}

	const paymentError = parsed["paymentError"] ?? parsed["PaymentError"];
	const paymentErrorMessage = isRecord(paymentError)
		? getTrimmedString(paymentError["message"], paymentError["Message"])
		: "";
	if (paymentErrorMessage.length > 0) {
		return [paymentErrorMessage];
	}

	const messageText = getTrimmedString(parsed["message"], parsed["Message"]);
	if (messageText.length > 0) {
		return [messageText];
	}

	const detailText = getTrimmedString(parsed["detail"], parsed["Detail"]);
	if (detailText.length > 0) {
		return [detailText];
	}

	return null;
};

/**
 * Extracts strings from a top-level array body. Supports two legacy formats:
 *   - `["Error 1", "Error 2"]`
 *   - `[{ message: "Error 1" }, { message: "Error 2" }]`
 * Each entry may also be a stringified JSON object containing
 * `{ message }` / `{ errors }`, which is unwrapped.
 *
 * Exported for use by feature-specific extractors (e.g. the LTL tender flow)
 * that need to handle legacy 400 response shapes; not used by the default
 * extraction path.
 */
export const extractFromTopLevelArray = (body: unknown[]): string[] => {
	const messages: string[] = [];
	for (const entry of body) {
		messages.push(...extractMessageFromEntry(entry));
	}
	return messages;
};

const buildApiError = (
	message: string,
	details: unknown,
	status: number | undefined,
): ApiError => {
	const result: ApiError = {
		success: false,
		error: message,
		details,
	};
	if (status !== undefined) {
		result.status = status;
	}
	return result;
};

const isGenericMessage = (message: string): boolean =>
	message.length === 0 || GENERIC_ERROR_MESSAGES.has(message.toLowerCase());

const extractFromObjectBody = (body: unknown): string => {
	if (typeof body === "string") {
		return body.trim();
	}

	return extractFromJson(body)?.join("\n") ?? "";
};

/**
 * Extracts error messages from HTTP API responses.
 * Prioritizes the `errors` array over the generic `message` field.
 *
 * Handles these JSON response body shapes:
 *   - `{ message: "...", errors: ["...", ...] }` (errors may be stringified JSON)
 *   - `{ message: "..." }`
 *   - `{ paymentError: { message: "..." } }`
 *
 * The HTTP status code is included on the returned `ApiError` whenever the
 * source error is a ky `HTTPError`, so callers can react to specific statuses
 * such as 412 Precondition Failed.
 *
 * @param error - The error object from the API call
 * @returns An ApiError object with the extracted message, or null if no valid error message is found
 *
 * @example
 * // API response: { "message": "Validation failed", "errors": ["Error 1", "Error 2"] }
 * // Returns: { success: false, error: "Error 1\nError 2", details: {...}, status: 400 }
 *
 * @example
 * // API response: {
 * //   "message": "Validation failed",
 * //   "errors": ["{\"message\":\"Validation failed\",\"errors\":[\"Duplicate customer commodities are not allowed\"]}"]
 * // }
 * // Returns: { success: false, error: "Duplicate customer commodities are not allowed", details: {...}, status: 400 }
 */
export const extractApiError = async (
	error: unknown,
): Promise<ApiError | null> => {
	if (!(error instanceof HTTPError)) {
		return null;
	}

	const status = error.response.status;

	try {
		const response = error.response.clone();
		const contentType = response.headers.get("content-type") ?? "";

		if (!isJsonContentType(contentType)) {
			return null;
		}

		const body: unknown = await response.json();

		const combinedMessage = extractFromObjectBody(body);
		if (isGenericMessage(combinedMessage)) {
			return null;
		}

		return buildApiError(combinedMessage, body, status);
	} catch (parseError) {
		console.error("Failed to parse API error response:", parseError);
	}

	return null;
};
